import React from "react";
import { FaTimes } from "react-icons/fa";
import axiosInstance from "../api/axiosInstance";
import { showGlobalToast } from '../utils/toast';

interface InvoicePreviewModalProps {
    invoiceHtml: string | null;
    selectedBill: {
        id: string;
        invoiceNo: string;
    } | null;
    onClose: () => void;
}

const InvoicePreviewModal: React.FC<InvoicePreviewModalProps> = ({
    invoiceHtml,
    selectedBill,
    onClose,
}) => {
    if (!invoiceHtml) return null;

    const handleDownloadPdf = async () => {
        if (!selectedBill || !invoiceHtml) return;
        try {
            const response = await axiosInstance.post(
                `/Billing/GetBillPdf/${selectedBill.invoiceNo}`,
                invoiceHtml,
                {
                    responseType: "blob",
                    headers: { "Content-Type": "application/json" },
                }
            );

            const url = window.URL.createObjectURL(
                new Blob([response.data], { type: "application/pdf" })
            );
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${selectedBill.invoiceNo}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            showGlobalToast("PDF downloaded successfully", "success");
        } catch (err) {
            console.error(err);
            showGlobalToast("Failed to download PDF", "error");
        }
    };

    const handlePrintInvoice = () => {
        const printWindow = window.open("", "_blank", "width=900,height=700");
        if (!printWindow) return;

        printWindow.document.write(`
      <html>
        <head>
          <title>Print Invoice</title>
          <style>
            body { background: #fff; color: #000; margin: 0; padding: 0; }
            @media print { body { -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>${invoiceHtml}</body>
      </html>
    `);

        printWindow.document.close();
        printWindow.focus();

        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 300);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-2 sm:p-4 z-50 transition-opacity duration-300">
            <div
                className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden 
          max-h-[90vh] flex flex-col relative transition-transform duration-300 animate-modal-in"
            >
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-gray-800 p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center z-10">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">
                        Invoice Preview
                    </h3>
                    <div className="flex items-center gap-2">
                        {/* Download PDF */}
                        <button
                            title="Download as PDF"
                            onClick={handleDownloadPdf}
                            className="text-blue-500 hover:text-blue-700 transition-colors px-2"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-6 h-6"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 16v-8m0 8l-3-3m3 3l3-3M4.5 19.5h15a2 2 0 002-2v-11a2 2 0 00-2-2h-15a2 2 0 00-2 2v11a2 2 0 002 2z"
                                />
                            </svg>
                        </button>

                        {/* Print */}
                        <button
                            title="Print Invoice"
                            onClick={handlePrintInvoice}
                            className="text-green-600 hover:text-green-800 transition-colors px-2"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-6 h-6"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M6 9V4.5A2.5 2.5 0 018.5 2h7A2.5 2.5 0 0118 4.5V9m-12 6.75V19a2 2 0 002 2h12a2 2 0 002-2v-3.25M3 15.75A2.25 2.25 0 015.25 13.5h13.5A2.25 2.25 0 0121 15.75v.75a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 16.5v-.75z"
                                />
                            </svg>
                        </button>

                        {/* Close */}
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors px-2"
                        >
                            <FaTimes size={22} className="sm:w-6 sm:h-6" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6 overflow-y-auto styled-scroll flex justify-center">
                    <div
                        id="invoice-html-content"
                        className="inline-block bg-white text-black dark:bg-white dark:text-black"
                        dangerouslySetInnerHTML={{ __html: invoiceHtml }}
                    />
                </div>
            </div>
        </div>
    );
};

export default InvoicePreviewModal;
